import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Sparkles, Zap, BookOpen, BarChart3, MessageSquare, LayoutTemplate, Layers, CheckCircle, ArrowRight, ShieldCheck, Calculator, Search as SearchIcon, FileText, Cpu, ChevronLeft, Tag } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { updateUser } from '../store/actions/authActions';
import toast from 'react-hot-toast';
import paymentService, { computeGst } from '../services/paymentService';
import { PriceBreakdown } from '../components/ui/PriceBreakdown';
import { BillingDetailsForm, validateBilling, BillingFormValues } from '../components/ui/BillingDetailsForm';

export const SubscriptionPricingPage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((state: RootState) => state.auth);
    const showFreePlan = !user || user?.plan === 'free';

    // Location state
    const [checkoutDetails, setCheckoutDetails] = useState<any>(null);

    // Billing form states
    const [starterBilling, setStarterBilling] = useState<BillingFormValues>({
        billingName: '',
        billingAddress: '',
        billingState: '',
        billingPan: '',
        billingGstin: '',
    });
    const [starterBillingErrors, setStarterBillingErrors] = useState<Partial<Record<keyof BillingFormValues, string>>>({});

    const [intelligenceBilling, setIntelligenceBilling] = useState<BillingFormValues>({
        billingName: '',
        billingAddress: '',
        billingState: '',
        billingPan: '',
        billingGstin: '',
    });
    const [intelligenceBillingErrors, setIntelligenceBillingErrors] = useState<Partial<Record<keyof BillingFormValues, string>>>({});

    // Billing modal states
    const [showBillingModal, setShowBillingModal] = useState(false);
    const [activePlanForModal, setActivePlanForModal] = useState<'starter' | 'intelligence' | null>(null);

    // Shared coupon state — one per plan type
    const [starterCoupon, setStarterCoupon] = useState('');
    const [intelligenceCoupon, setIntelligenceCoupon] = useState('');
    const [appliedStarterCoupon, setAppliedStarterCoupon] = useState<any>(null);
    const [appliedIntelligenceCoupon, setAppliedIntelligenceCoupon] = useState<any>(null);
    const [starterCouponError, setStarterCouponError] = useState('');
    const [intelligenceCouponError, setIntelligenceCouponError] = useState('');
    const [isValidatingStarter, setIsValidatingStarter] = useState(false);
    const [isValidatingIntelligence, setIsValidatingIntelligence] = useState(false);
    const [isProcessingStarter, setIsProcessingStarter] = useState(false);
    const [isProcessingIntelligence, setIsProcessingIntelligence] = useState(false);
    const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);

    useEffect(() => {
        const fetchCheckoutDetails = async () => {
            try {
                const res = await paymentService.getCheckoutDetails();
                if (res.success && res.data) {
                    setCheckoutDetails(res.data);
                    const stateName = res.data.location?.region || 'Tamil Nadu';
                    setStarterBilling((prev) => ({ ...prev, billingState: stateName }));
                    setIntelligenceBilling((prev) => ({ ...prev, billingState: stateName }));
                }
            } catch (err) {
                console.error('Failed to fetch checkout location details:', err);
            }
        };
        fetchCheckoutDetails();
    }, []);

    const isIndia = checkoutDetails?.countryCode === 'IN' || 
                    checkoutDetails?.location?.countryCode === 'IN' || 
                    checkoutDetails?.location?.country?.toLowerCase() === 'india' || 
                    false;

    const getPlanPricing = (plan: 'starter' | 'intelligence') => {
        if (isIndia) {
            const basePrice = plan === 'starter' ? 2199 : 9999;
            const coupon = plan === 'starter' ? appliedStarterCoupon : appliedIntelligenceCoupon;
            const discountPercent = coupon?.discountPercent ?? 0;
            const billingVals = plan === 'starter' ? starterBilling : intelligenceBilling;
            const gst = computeGst(basePrice, discountPercent, billingVals.billingState, billingVals.billingGstin);
            return {
                basePrice,
                discountPercent,
                discountAmount: gst.discountAmount,
                priceAfterDiscount: gst.priceAfterDiscount,
                taxAmount: gst.taxAmount,
                finalPrice: gst.finalInclTax,
                currency: 'INR',
                symbol: '₹',
            };
        } else {
            const basePrice = plan === 'starter' ? 20 : 49;
            const coupon = plan === 'starter' ? appliedStarterCoupon : appliedIntelligenceCoupon;
            const discountPercent = coupon?.discountPercent ?? 0;
            const discountAmount = basePrice * (discountPercent / 100);
            const finalPrice = basePrice - discountAmount;
            return {
                basePrice,
                discountPercent,
                discountAmount,
                priceAfterDiscount: finalPrice,
                taxAmount: 0,
                finalPrice,
                currency: 'USD',
                symbol: '$',
            };
        }
    };

    useEffect(() => {
        if (!(window as any).Razorpay) {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.async = true;
            script.onload = () => setIsRazorpayLoaded(true);
            script.onerror = () => toast.error('Razorpay SDK failed to load.');
            document.body.appendChild(script);
            return () => { if (document.body.contains(script)) document.body.removeChild(script); };
        } else {
            setIsRazorpayLoaded(true);
        }
    }, []);

    const handleValidateCoupon = async (plan: 'starter' | 'intelligence') => {
        const code = plan === 'starter' ? starterCoupon : intelligenceCoupon;
        if (!code.trim()) {
            plan === 'starter' ? setStarterCouponError('Enter a coupon code') : setIntelligenceCouponError('Enter a coupon code');
            return;
        }
        plan === 'starter' ? setIsValidatingStarter(true) : setIsValidatingIntelligence(true);
        plan === 'starter' ? setStarterCouponError('') : setIntelligenceCouponError('');

        try {
            const res = await paymentService.validateCoupon(code, plan);
            if (res.success && res.data) {
                if (plan === 'starter') {
                    setAppliedStarterCoupon(res.data);
                    toast.success(`✓ ${res.data.discountPercent}% discount applied!`);
                } else {
                    setAppliedIntelligenceCoupon(res.data);
                    toast.success(`✓ ${res.data.discountPercent}% discount applied!`);
                }
            } else {
                if (plan === 'starter') { setStarterCouponError(res.message || 'Invalid coupon'); setAppliedStarterCoupon(null); }
                else { setIntelligenceCouponError(res.message || 'Invalid coupon'); setAppliedIntelligenceCoupon(null); }
            }
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Failed to validate coupon';
            if (plan === 'starter') { setStarterCouponError(msg); setAppliedStarterCoupon(null); }
            else { setIntelligenceCouponError(msg); setAppliedIntelligenceCoupon(null); }
        } finally {
            plan === 'starter' ? setIsValidatingStarter(false) : setIsValidatingIntelligence(false);
        }
    };

    const handleClearCoupon = (plan: 'starter' | 'intelligence') => {
        if (plan === 'starter') { setAppliedStarterCoupon(null); setStarterCoupon(''); setStarterCouponError(''); }
        else { setAppliedIntelligenceCoupon(null); setIntelligenceCoupon(''); setIntelligenceCouponError(''); }
    };

    const handleCheckout = async (plan: 'starter' | 'intelligence', bypassModal = false) => {
        if (isIndia && !bypassModal) {
            setActivePlanForModal(plan);
            setShowBillingModal(true);
            return;
        }

        const pricing = getPlanPricing(plan);
        const couponData = plan === 'starter' ? appliedStarterCoupon : appliedIntelligenceCoupon;
        const couponCode = couponData?.couponName;

        let billingPayload: any = undefined;
        if (isIndia) {
            const billingVals = plan === 'starter' ? starterBilling : intelligenceBilling;
            const setErrors = plan === 'starter' ? setStarterBillingErrors : setIntelligenceBillingErrors;
            const errs = validateBilling(billingVals);
            if (Object.keys(errs).length > 0) {
                setErrors(errs);
                toast.error('Please complete billing details.');
                return;
            }
            billingPayload = {
                billingState: billingVals.billingState,
                billingPan: billingVals.billingPan,
                billingGstin: billingVals.billingGstin,
                billingAddress: billingVals.billingAddress,
            };
            setShowBillingModal(false);
        }

        const setProcessing = plan === 'starter' ? setIsProcessingStarter : setIsProcessingIntelligence;
        setProcessing(true);

        try {
            if (!isRazorpayLoaded) {
                toast.error('Payment gateway is initializing. Please try again.');
                setProcessing(false);
                return;
            }

            // Create Razorpay Order on Backend
            const orderRes = await paymentService.createOrder(plan, couponCode, billingPayload);
            if (!orderRes.success) {
                toast.error(orderRes.message || 'Failed to initiate payment.');
                setProcessing(false);
                return;
            }

            // If order bypass is requested (100% coupon)
            if (orderRes.bypass) {
                const result = await paymentService.upgradePlan(plan, undefined, couponCode, billingPayload);
                if (result.success) {
                    toast.success('🎉 Plan activated! Resources provisioned.');
                    dispatch(updateUser({
                        ...(user as any),
                        planType: plan,
                        tokensRemaining: result.data?.tokensRemaining ?? (user as any)?.tokensRemaining,
                        tokensTotal: result.data?.tokensTotal ?? (user as any)?.tokensTotal,
                        projectsAllowed: result.data?.projectsAllowed ?? (user as any)?.projectsAllowed,
                    } as any));
                    navigate('/dashboard');
                } else {
                    toast.error(result.message || 'Transaction failed');
                }
                setProcessing(false);
                return;
            }

            const options = {
                key: orderRes.key_id || '',
                amount: orderRes.amount,
                currency: orderRes.currency || 'INR',
                name: 'DocSprintAI',
                description: `${plan === 'starter' ? 'Growth' : 'Intelligence'} Plan${pricing.discountPercent > 0 ? ` (${pricing.discountPercent}% off)` : ''}`,
                image: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciIHgxPSIwIiB5MT0iMCIgeDI9IjEiIHkyPSIxIj4KICAgICAgPHN0b3Agb2Zmc2V0PSIwIiBzdG9wLWNvbG9yPSIjZmI5MjNjIi8+CiAgICAgIDxzdG9wIG9mZnNldD0iMSIgc3RvcC1jb2xvcj0iI2VhNTgwYyIvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIHJ4PSI0MCIgZmlsbD0idXJsKCNnKSIvPgogIDwhLS0gQ2VudGVyIFN0YXIgLS0+CiAgPHBhdGggZD0iTTEwMCA0MCBMMTE1IDg1IEwxNjAgMTAwIEwxMTUgMTE1IEwxMDAgMTYwIEw4NSAxMTUgTDQwIDEwMCBMODUgODUgWiIgZmlsbD0id2hpdGUiLz4KICA8IS0tIFNtYWxsIFN0YXIgLS0+CiAgPHBhdGggZD0iTTE2MCA0MCBMMTY1IDU1IEwxODAgNjAgTDE2NSA2NSBMMTYwIDgwIEwxNTUgNjUgTDE0MCA2MCBMMTU1IDU1IFoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPg==",
                order_id: orderRes.order_id,
                handler: async function (rzpResponse: any) {
                    try {
                        const result = await paymentService.verifyPayment(
                            rzpResponse.razorpay_payment_id,
                            rzpResponse.razorpay_order_id,
                            rzpResponse.razorpay_signature
                        );
                        if (result.success) {
                            toast.success('🎉 Payment successful! Resources provisioned.');
                            dispatch(updateUser({
                                ...(user as any),
                                planType: plan,
                                tokensRemaining: result.data?.tokensRemaining ?? (user as any)?.tokensRemaining,
                                tokensTotal: result.data?.tokensTotal ?? (user as any)?.tokensTotal,
                                projectsAllowed: result.data?.projectsAllowed ?? (user as any)?.projectsAllowed,
                            } as any));
                            navigate('/dashboard');
                        } else {
                            toast.error(result.message || 'Failed to provision resources');
                        }
                    } catch {
                        toast.error('Payment succeeded but provisioning failed. Contact support.');
                    } finally {
                        setProcessing(false);
                    }
                },
                prefill: {
                    name: (user?.firstName && user?.lastName) ? `${user.firstName} ${user.lastName}` : (user as any)?.username || 'User',
                    email: (user as any)?.email || '',
                    contact: (user as any)?.contactNumber || '',
                },
                theme: { color: plan === 'intelligence' ? '#f97316' : '#10b981' },
                modal: { ondismiss: () => setProcessing(false) },
            };

            const rzp = new (window as any).Razorpay(options);
            rzp.open();
            rzp.on('payment.failed', (resp: any) => {
                toast.error(resp.error.description || 'Payment failed');
                setProcessing(false);
            });
        } catch (err: any) {
            console.error('Checkout error:', err);
            toast.error('Failed to initiate payment.');
            setProcessing(false);
        }
    };

    return (
        <div className="min-h-screen bg-app-bg font-sans text-white relative overflow-hidden selection:bg-primary/30">
            {/* Ambient Background */}
            <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[150px] -z-10 animate-pulse-slow"></div>
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-orange-900/10 rounded-full blur-[120px] -z-10"></div>
            <div className="absolute top-1/4 left-1/4 w-px h-px shadow-[0_0_100px_40px_rgba(255,107,0,0.05)] -z-10"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="fixed top-8 left-8 p-3 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 shadow-2xl hover:bg-white/10 hover:border-white/20 transition-all group z-50 lg:flex hidden items-center gap-2 pr-6"
                >
                    <ChevronLeft className="w-5 h-5 text-text-secondary group-hover:-translate-x-1 transition-transform" />
                    <span className="text-sm font-black text-text-secondary uppercase tracking-widest">System Back</span>
                </button>

                {/* Mobile Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="lg:hidden p-2 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 mb-6 flex items-center gap-2"
                >
                    <ChevronLeft className="w-5 h-5 text-text-secondary" />
                    <span className="text-sm font-bold text-text-secondary">Back</span>
                </button>

                {/* Step Nav */}
                <div className="flex justify-center mb-16">
                    <div className="flex items-center gap-6 bg-[#131B2C]/50 backdrop-blur-xl px-6 py-3 rounded-2xl border border-white/5 shadow-2xl">
                        <div className="flex items-center gap-2.5">
                            <div className="w-6 h-6 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center border border-green-500/30">
                                <CheckCircle size={12} fill="currentColor" className="text-green-500/40" />
                            </div>
                            <span className="text-[10px] font-black text-text-secondary uppercase tracking-[0.2em]">Validated</span>
                        </div>
                        <div className="w-8 h-[1px] bg-white/10"></div>
                        <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center font-black text-[10px] shadow-[0_0_15px_rgba(255,107,0,0.4)] ring-4 ring-primary/20">2</div>
                            <span className="text-[10px] font-black text-white uppercase tracking-[0.2em]">Select Plan</span>
                        </div>
                        <div className="w-8 h-[1px] bg-white/10"></div>
                        <div className="flex items-center gap-2.5 opacity-50">
                            <div className="w-6 h-6 rounded-full bg-white/5 text-text-secondary flex items-center justify-center font-black text-[10px] border border-white/10">3</div>
                            <span className="text-[10px] font-black text-text-secondary uppercase tracking-[0.2em]">Project</span>
                        </div>
                    </div>
                </div>

                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-20 animate-in fade-in slide-in-from-top-4 duration-700">
                    <div className="flex justify-center mb-0">
                        <Link to="/" className="flex items-center gap-3">
                            <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-32 w-auto invert brightness-0" />
                        </Link>
                    </div>
                    <div className="inline-flex items-center gap-2 text-primary font-black tracking-[0.2em] uppercase text-[9px] border border-primary/20 bg-primary/5 px-4 py-1.5 rounded-full mb-6 shadow-[0_0_20px_rgba(255,107,0,0.1)]">
                        <ShieldCheck size={12} /> SECURE ENCRYPTED PROVISIONING
                    </div>
                    <h1 className="text-5xl md:text-6xl font-black font-heading mb-6 text-white tracking-tight">
                        Fuel Your <span className="bg-gradient-to-r from-primary via-orange-400 to-amber-400 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(255,107,0,0.3)]">Intelligence</span>
                    </h1>
                    <p className="text-xl text-text-secondary font-medium leading-relaxed max-w-2xl mx-auto mb-6">
                        High-density AI tokens with zero expiration. Provision what you need, scale when you must.
                    </p>
                    {isIndia && checkoutDetails?.location && (
                        <div className="inline-flex items-center gap-2 text-emerald-400 font-black tracking-wider text-[10px] border border-emerald-500/20 bg-emerald-500/5 px-4 py-1.5 rounded-full shadow-[0_0_20px_rgba(16,185,129,0.1)] uppercase">
                            📍 Location Detected: {checkoutDetails.location.city}, {checkoutDetails.location.region}, {checkoutDetails.location.country} (INR Prices Apply)
                        </div>
                    )}
                </div>

                {/* Plans Grid */}
                <div className={`grid grid-cols-1 ${showFreePlan ? 'md:grid-cols-3 max-w-6xl' : 'md:grid-cols-2 max-w-4xl'} gap-8 mx-auto items-stretch relative`}>

                    {/* Free Plan */}
                    {showFreePlan && (
                        <div className="flex flex-col p-8 rounded-[2rem] bg-[#131B2C]/30 backdrop-blur-xl border border-white/5 shadow-2xl hover:border-white/10 transition-all duration-500 hover:-translate-y-2 relative overflow-hidden group">
                            <div className="absolute top-0 left-0 w-full h-1 bg-white/5 group-hover:bg-white/20 transition-colors"></div>
                            <div className="mb-8">
                                <div className="inline-flex px-3 py-1 rounded-full bg-white/5 text-text-secondary text-[9px] font-black uppercase tracking-widest mb-4 border border-white/5">Default</div>
                                <h3 className="text-2xl font-black text-white mb-1 uppercase tracking-tight text-shadow">Explorer</h3>
                                <p className="text-text-secondary text-xs font-bold uppercase tracking-wider opacity-60">Entry level plan</p>
                            </div>
                            <div className="flex items-baseline mb-8">
                                <span className="text-6xl font-black text-white">$0</span>
                                <span className="text-text-secondary ml-2 text-sm font-black uppercase tracking-widest opacity-40">/ Lifetime</span>
                            </div>
                            <div className="bg-white/5 rounded-2xl p-4 mb-8 border border-white/5 space-y-2">
                                <div className="flex justify-between text-[11px] font-black text-text-secondary uppercase tracking-widest"><span>Compute</span><span className="text-white">100K Tokens</span></div>
                                <div className="flex justify-between text-[11px] font-black text-text-secondary uppercase tracking-widest"><span>Projects</span><span className="text-white">1 Active Workspace</span></div>
                            </div>
                            <div className="space-y-4 mb-10 flex-1 text-left">
                                {[
                                    { text: '100K Generative Tokens', icon: BookOpen },
                                    { text: '1 Active Project', icon: Layers },
                                    { text: 'Standard AI Orchestration', icon: BookOpen },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm text-text-secondary group-hover:text-white/80 transition-colors">
                                        <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 bg-white/5 text-text-secondary group-hover:text-primary transition-colors border border-white/5 shadow-inner">
                                            <item.icon size={16} />
                                        </div>
                                        <span className="font-bold text-xs uppercase tracking-wider">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                            <Button
                                onClick={async () => {
                                    try {
                                        await paymentService.updateSubscriptionStatus('F');
                                        dispatch(updateUser({ ...(user || {}), subscribed: 'F', planType: 'free' } as any));
                                        navigate('/dashboard');
                                    } catch {
                                        navigate('/login');
                                    }
                                }}
                                variant="outline"
                                className="w-full h-14 rounded-2xl border-white/10 text-white font-black uppercase tracking-[0.2em] hover:bg-white/5 transition-all flex items-center justify-center gap-2 group/btn"
                            >
                                {user ? 'Continue to Dashboard' : 'Activate System'} <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                            </Button>
                        </div>
                    )}

                    {/* Starter / Growth Plan */}
                    <div className="flex flex-col p-8 rounded-[2rem] bg-[#131B2C]/40 backdrop-blur-xl border border-emerald-500/10 shadow-2xl hover:border-emerald-500/30 transition-all duration-500 hover:-translate-y-2 relative overflow-hidden group">
                        <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500/20 group-hover:bg-emerald-500 transition-colors"></div>
                        <div className="mb-8">
                            <div className="inline-flex px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-[9px] font-black uppercase tracking-widest mb-4 border border-emerald-500/20">Advanced</div>
                            <h3 className="text-2xl font-black text-white mb-1 uppercase tracking-tight text-shadow">Growth</h3>
                            <p className="text-text-secondary text-xs font-bold uppercase tracking-wider opacity-60">High-performance tier</p>
                        </div>
                        <div className="flex items-baseline mb-2">
                            <span className="text-6xl font-black text-white font-heading">
                                {getPlanPricing('starter').symbol}
                                {isIndia 
                                  ? getPlanPricing('starter').basePrice.toLocaleString('en-IN') 
                                  : getPlanPricing('starter').basePrice.toFixed(0)
                                }
                            </span>
                            <span className="text-emerald-500/60 ml-2 text-sm font-black uppercase tracking-widest">/ 1M Tokens</span>
                        </div>
                        {isIndia && (
                            <div className="mb-4">
                                <PriceBreakdown
                                    basePrice={2199}
                                    discountPercent={appliedStarterCoupon?.discountPercent ?? 0}
                                    billingState={starterBilling.billingState}
                                    billingGstin={starterBilling.billingGstin}
                                    accentColor="emerald"
                                />
                            </div>
                        )}
                        {appliedStarterCoupon && (
                            <div className="flex items-center gap-2 mb-5 mt-1">
                                <span className="line-through text-text-secondary text-sm font-bold">
                                    {getPlanPricing('starter').symbol}
                                    {getPlanPricing('starter').basePrice}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[10px] font-black uppercase border border-emerald-500/25">
                                    -{appliedStarterCoupon.discountPercent}% OFF
                                </span>
                            </div>
                        )}
                        <div className="bg-emerald-500/5 rounded-2xl p-4 mb-8 border border-emerald-500/10 space-y-2">
                            <div className="flex justify-between text-[11px] font-black text-emerald-400/80 uppercase tracking-widest"><span>Compute</span><span className="text-white">1M Tokens</span></div>
                            <div className="flex justify-between text-[11px] font-black text-emerald-400/80 uppercase tracking-widest"><span>Projects</span><span className="text-white">3 Projects</span></div>
                        </div>
                        <div className="space-y-4 mb-8 flex-1 text-left">
                            {[
                                { text: '1M Generative Tokens', icon: Cpu },
                                { text: '3 Concurrent Projects', icon: Layers },
                                { text: 'Full AI Intelligence Suite', icon: Sparkles },
                                { text: 'Structure Visualizer', icon: LayoutTemplate },
                            ].map((item, i) => (
                                <div key={i} className="flex items-center gap-3 text-sm text-text-secondary group-hover:text-white/90 transition-colors">
                                    <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 bg-emerald-500/10 text-emerald-400 group-hover:text-emerald-300 transition-colors border border-emerald-500/10 shadow-inner">
                                        <item.icon size={16} />
                                    </div>
                                    <span className="font-bold text-xs uppercase tracking-wider">{item.text}</span>
                                </div>
                            ))}
                        </div>

                        {/* Coupon Input */}
                        <div className="mb-4 bg-black/20 p-3 rounded-xl border border-emerald-500/10 space-y-2">
                            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-text-secondary mb-1">
                                <Tag size={10} className="text-emerald-400" />
                                <span>Promo Code</span>
                            </div>
                            <div className="flex gap-2">
                                <input
                                    id="starter-coupon-input"
                                    type="text"
                                    placeholder="ENTER CODE"
                                    value={starterCoupon}
                                    onChange={(e) => { setStarterCoupon(e.target.value.toUpperCase()); setStarterCouponError(''); }}
                                    onKeyDown={(e) => e.key === 'Enter' && !appliedStarterCoupon && handleValidateCoupon('starter')}
                                    disabled={isValidatingStarter || isProcessingStarter || !!appliedStarterCoupon}
                                    className="bg-[#0B0F19]/80 border border-white/10 text-white rounded-lg px-3 py-2 text-[10px] font-bold uppercase tracking-wider focus:outline-none focus:border-emerald-500 flex-1 placeholder-white/20 transition-colors"
                                />
                                {appliedStarterCoupon ? (
                                    <button
                                        onClick={() => handleClearCoupon('starter')}
                                        className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg px-3 text-[10px] font-black uppercase tracking-wider transition-colors"
                                    >
                                        Clear
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => handleValidateCoupon('starter')}
                                        disabled={isValidatingStarter || isProcessingStarter}
                                        className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 rounded-lg px-3 text-[10px] font-black uppercase tracking-wider transition-colors disabled:opacity-50"
                                    >
                                        {isValidatingStarter ? '...' : 'Apply'}
                                    </button>
                                )}
                            </div>
                            {starterCouponError && (
                                <p className="text-[9px] font-bold text-red-400 uppercase tracking-wider">{starterCouponError}</p>
                            )}
                            {appliedStarterCoupon && (
                                <p className="text-[9px] font-bold text-emerald-400 uppercase tracking-wider">✓ Coupon applied successfully</p>
                            )}
                        </div>



                        <Button
                            id="starter-checkout-btn"
                            onClick={() => handleCheckout('starter')}
                            disabled={isProcessingStarter}
                            loading={isProcessingStarter}
                            className="w-full h-14 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black uppercase tracking-[0.2em] hover:brightness-110 shadow-[0_0_20px_rgba(10,185,129,0.3)] transition-all flex items-center justify-center gap-2 border-none"
                        >
                            {appliedStarterCoupon?.discountPercent === 100 ? (
                                <>Claim Free Growth <Sparkles size={16} /></>
                            ) : (
                                <>Provision Growth <ArrowRight size={16} /></>
                            )}
                        </Button>
                    </div>

                    {/* Intelligence Plan */}
                    <div className="flex flex-col p-8 rounded-[2rem] bg-[#161F33] text-white shadow-[0_0_50px_rgba(0,0,0,0.5)] relative transition-all duration-500 hover:-translate-y-2 z-10 border border-primary/20 group scale-105 ring-1 ring-primary/30">
                        <div className="absolute -inset-[1px] bg-gradient-to-r from-primary via-orange-500 to-amber-500 rounded-[2.1rem] -z-10 opacity-30 group-hover:opacity-50 blur-[2px] transition-opacity"></div>
                        <div className="absolute top-0 right-0 p-1">
                            <div className="bg-gradient-to-r from-primary via-orange-500 to-amber-500 text-white text-[9px] font-black px-5 py-2 rounded-bl-2xl rounded-tr-[1.8rem] uppercase tracking-[0.3em] shadow-2xl flex items-center gap-2">
                                <Sparkles size={10} fill="currentColor" /> ELITE PERFORMANCE
                            </div>
                        </div>
                        <div className="mb-8 mt-4">
                            <div className="inline-flex px-3 py-1 rounded-full bg-primary/20 text-primary text-[9px] font-black uppercase tracking-widest mb-4 border border-primary/30 shadow-[0_0_15px_rgba(255,107,0,0.1)]">Elite Integration</div>
                            <h3 className="text-2xl font-black text-white mb-1 uppercase tracking-tight text-shadow-glow">Intelligence</h3>
                            <p className="text-text-secondary text-xs font-bold uppercase tracking-wider opacity-60">Full stack AI enterprise</p>
                        </div>
                        <div className="flex items-baseline mb-2">
                            <span className="text-6xl font-black text-white drop-shadow-[0_0_10px_rgba(255,107,0,0.2)]">
                                {getPlanPricing('intelligence').symbol}
                                {isIndia 
                                  ? getPlanPricing('intelligence').basePrice.toLocaleString('en-IN') 
                                  : getPlanPricing('intelligence').basePrice.toFixed(0)
                                }
                            </span>
                            <span className="text-primary/60 ml-2 text-sm font-black uppercase tracking-widest">/ 5M Tokens</span>
                        </div>
                        {isIndia && (
                            <div className="mb-4">
                                <PriceBreakdown
                                    basePrice={9999}
                                    discountPercent={appliedIntelligenceCoupon?.discountPercent ?? 0}
                                    billingState={intelligenceBilling.billingState}
                                    billingGstin={intelligenceBilling.billingGstin}
                                    accentColor="orange"
                                />
                            </div>
                        )}
                        {appliedIntelligenceCoupon && (
                            <div className="flex items-center gap-2 mb-5 mt-1">
                                <span className="line-through text-text-secondary text-sm font-bold">
                                    {getPlanPricing('intelligence').symbol}
                                    {getPlanPricing('intelligence').basePrice}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-black uppercase border border-primary/25">
                                    -{appliedIntelligenceCoupon.discountPercent}% OFF
                                </span>
                            </div>
                        )}
                        <div className="bg-primary/10 rounded-2xl p-4 mb-8 border border-primary/20 space-y-2 relative overflow-hidden">
                            <div className="absolute inset-0 bg-primary/5 animate-pulse"></div>
                            <div className="relative flex justify-between text-[11px] font-black text-primary uppercase tracking-widest"><span>Compute</span><span className="text-white">5M Tokens</span></div>
                            <div className="relative flex justify-between text-[11px] font-black text-primary uppercase tracking-widest"><span>Projects</span><span className="text-white">UNLIMITED</span></div>
                        </div>
                        <div className="space-y-4 mb-8 flex-1">
                            {[
                                { text: '5M Generative Tokens', icon: Cpu },
                                { text: 'Unlimited Projects', icon: Layers },
                                { text: 'Elite Product Analysis', icon: BarChart3 },
                                { text: 'Market Velocity Scanning', icon: SearchIcon },
                                { text: 'Enterprise Deck Synthesis', icon: FileText },
                                { text: 'Priority Support', icon: CheckCircle },
                            ].map((item, i) => (
                                <div key={i} className="flex items-center gap-3 text-sm text-white group-hover:text-primary transition-colors">
                                    <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 bg-primary/10 text-primary border border-primary/20 shadow-[0_0_10px_rgba(255,107,0,0.1)]">
                                        <item.icon size={16} />
                                    </div>
                                    <span className="font-black text-[10px] uppercase tracking-widest">{item.text}</span>
                                </div>
                            ))}
                        </div>

                        {/* Coupon Input */}
                        <div className="mb-4 bg-black/20 p-3 rounded-xl border border-primary/10 space-y-2">
                            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-text-secondary mb-1">
                                <Tag size={10} className="text-primary" />
                                <span>Promo Code</span>
                            </div>
                            <div className="flex gap-2">
                                <input
                                    id="intelligence-coupon-input"
                                    type="text"
                                    placeholder="ENTER CODE"
                                    value={intelligenceCoupon}
                                    onChange={(e) => { setIntelligenceCoupon(e.target.value.toUpperCase()); setIntelligenceCouponError(''); }}
                                    onKeyDown={(e) => e.key === 'Enter' && !appliedIntelligenceCoupon && handleValidateCoupon('intelligence')}
                                    disabled={isValidatingIntelligence || isProcessingIntelligence || !!appliedIntelligenceCoupon}
                                    className="bg-[#0B0F19]/80 border border-white/10 text-white rounded-lg px-3 py-2 text-[10px] font-bold uppercase tracking-wider focus:outline-none focus:border-primary flex-1 placeholder-white/20 transition-colors"
                                />
                                {appliedIntelligenceCoupon ? (
                                    <button
                                        onClick={() => handleClearCoupon('intelligence')}
                                        className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg px-3 text-[10px] font-black uppercase tracking-wider transition-colors"
                                    >
                                        Clear
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => handleValidateCoupon('intelligence')}
                                        disabled={isValidatingIntelligence || isProcessingIntelligence}
                                        className="bg-primary/20 hover:bg-primary/30 text-primary rounded-lg px-3 text-[10px] font-black uppercase tracking-wider transition-colors disabled:opacity-50"
                                    >
                                        {isValidatingIntelligence ? '...' : 'Apply'}
                                    </button>
                                )}
                            </div>
                            {intelligenceCouponError && (
                                <p className="text-[9px] font-bold text-red-400 uppercase tracking-wider">{intelligenceCouponError}</p>
                            )}
                            {appliedIntelligenceCoupon && (
                                <p className="text-[9px] font-bold text-primary uppercase tracking-wider">✓ Coupon applied successfully</p>
                            )}
                        </div>



                        <Button
                            id="intelligence-checkout-btn"
                            onClick={() => handleCheckout('intelligence')}
                            disabled={isProcessingIntelligence}
                            loading={isProcessingIntelligence}
                            className="w-full h-16 rounded-2xl bg-gradient-to-r from-primary via-orange-500 to-amber-500 text-white font-black uppercase tracking-[0.3em] hover:brightness-125 shadow-[0_0_30px_rgba(255,107,0,0.4)] transition-all flex items-center justify-center gap-2 border-none active:scale-[0.98] group/btn"
                        >
                            {appliedIntelligenceCoupon?.discountPercent === 100 ? (
                                <>Claim Free Elite <Sparkles size={18} /></>
                            ) : (
                                <>INITIALIZE ELITE <ArrowRight size={18} className="group-hover/btn:translate-x-2 transition-transform" /></>
                            )}
                        </Button>
                    </div>
                </div>

                <div className="mt-20 text-center animate-in fade-in duration-1000 delay-500">
                    <p className="text-text-secondary text-xs font-black uppercase tracking-[0.3em] opacity-50">
                        Zero token expiration. Scalable architecture. <Link to="/custom-pricing" className="text-primary hover:text-orange-400 transition-colors underline-offset-8 underline decoration-primary/30">Request Enterprise Node</Link>
                    </p>
                </div>
            </div>

            {/* Billing Information Modal */}
            {showBillingModal && activePlanForModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-200">
                    <div className="w-full max-w-md rounded-[2rem] bg-[#131B2C]/95 border border-white/10 p-6 shadow-2xl space-y-5 text-left relative">
                        <div>
                            <h3 className="text-xl font-black text-white uppercase tracking-tight">Billing Information</h3>
                            <p className="text-xs text-text-secondary font-medium">Please provide your billing details to generate the invoice.</p>
                        </div>

                        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                            <BillingDetailsForm
                                values={activePlanForModal === 'starter' ? starterBilling : intelligenceBilling}
                                onChange={activePlanForModal === 'starter' ? setStarterBilling : setIntelligenceBilling}
                                errors={activePlanForModal === 'starter' ? starterBillingErrors : intelligenceBillingErrors}
                                accentColor={activePlanForModal === 'starter' ? 'emerald' : 'orange'}
                            />
                        </div>

                        <div className="flex items-center gap-3 pt-3 border-t border-white/5">
                            <button
                                onClick={() => setShowBillingModal(false)}
                                className="flex-1 h-12 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-xs uppercase tracking-wider transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    // Submit and proceed
                                    handleCheckout(activePlanForModal, true);
                                }}
                                className={`flex-1 h-12 rounded-xl text-white font-black text-xs uppercase tracking-widest transition-all ${
                                    activePlanForModal === 'starter'
                                        ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:brightness-110 shadow-[0_0_15px_rgba(10,185,129,0.3)]'
                                        : 'bg-gradient-to-r from-primary to-orange-500 hover:brightness-110 shadow-[0_0_15px_rgba(255,107,0,0.3)]'
                                }`}
                            >
                                {getPlanPricing(activePlanForModal).finalPrice === 0
                                    ? (activePlanForModal === 'starter' ? 'Claim Free Growth' : 'Claim Free Elite')
                                    : 'Proceed to Payment'
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
